import React, { useEffect, useMemo, useState } from "react";
import moment, { Moment } from "moment";
import { DATE_FORMAT } from "utils";

export type CalendarProps = {
    selected?: string | Date | Moment | null;
    onSelect?: (date: Moment) => void;
    weekDayFormat?: string;
    weekMode?: boolean;
    renderContent?: (date: Moment) => React.ReactNode;
    disabledDate?: (date: Moment) => boolean;
    colorDate?: (date: Moment) => "default" | "success" | "active" | "info" | "warning" | "error";
    onMonthChange?: (date: Moment) => void;
    shadow?: boolean;
};

export default function AppCalendar({
    selected = null,
    onSelect,
    weekDayFormat = "ddd",
    weekMode = false,
    renderContent,
    disabledDate,
    colorDate,
    onMonthChange,
    shadow = true
}: CalendarProps) {
    const today = useMemo(() => moment(), []);
    const [currentMonth, setCurrentMonth] = useState<Moment>(
        selected ? moment(selected) : moment()
    );
    useEffect(() => {
        onMonthChange?.(currentMonth);
    }, [currentMonth])
    const selectedMoment = selected ? moment(selected) : null;

    const calendarDays = useMemo(() => {
        if (weekMode) {
            const baseDate = selectedMoment ? selectedMoment.clone() : currentMonth.clone();
            const startOfWeek = baseDate.clone().startOf("week");
            return Array.from({ length: 7 }).map((_, i) =>
                startOfWeek.clone().add(i, "day")
            );
        } else {
            const monthStart = currentMonth.clone().startOf("month");
            const monthEnd = currentMonth.clone().endOf("month");
            const startDate = monthStart.clone().startOf("week");
            const endDate = monthEnd.clone().endOf("week");

            const days: Moment[] = [];
            let day = startDate.clone();
            while (day.isSameOrBefore(endDate, "day")) {
                days.push(day.clone());
                day.add(1, "day");
            }
            return days;
        }
    }, [currentMonth, selectedMoment, weekMode]);

    const weekdays = useMemo(() => {
        const start = moment().startOf("week");
        return Array.from({ length: 7 }).map((_, i) => start.clone().add(i, "day"));
    }, []);

    function prevMonth() {
        setCurrentMonth((m) => m.clone().subtract(1, "month"));
    }
    function nextMonth() {
        setCurrentMonth((m) => m.clone().add(1, "month"));
    }

    function handleSelect(d: Moment) {
        if (disabledDate?.(d)) return;
        onSelect && onSelect(d.clone());
    }

    const colorMap: Record<string, string> = {
        success: "bg-green-200 text-green-900 border-green-400",
        active: "bg-blue-200 text-blue-900 border-blue-400",
        info: "bg-cyan-200 text-cyan-900  border-cyan-400",
        warning: "bg-yellow-200 text-yellow-900  border-yellow-400",
        error: "bg-red-100 text-red-900 border-red-200",
    };

    return (
        <div className={`w-full bg-white rounded p-4 border border-gray-200 ${shadow ? "shadow" : ""}`}>
            {/* Header */}
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-200">
                <div>
                    <div className="text-lg font-semibold">
                        {weekMode
                            ? (selectedMoment || currentMonth).format("MMMM YYYY")
                            : currentMonth.format("MMMM YYYY")}
                    </div>
                </div>
                {!weekMode && (
                    <div className="flex gap-2">
                        <button
                            aria-label="Previous month"
                            onClick={prevMonth}
                            className="px-2 rounded-lg hover:bg-gray-100 border border-gray-300"
                        >
                            ◀
                        </button>
                        <button
                            aria-label="Next month"
                            onClick={nextMonth}
                            className="px-2 rounded-lg hover:bg-gray-100 border border-gray-300"
                        >
                            ▶
                        </button>
                    </div>
                )}
            </div>

            {/* Weekday headers */}
            <div className="grid grid-cols-7 text-center text-sm font-medium text-gray-500 mb-2 border border-gray-200 py-2 rounded">
                {weekdays.map((w) => (
                    <div key={w.format("dd")}>{w.format(weekDayFormat)}</div>
                ))}
            </div>

            {/* Days grid */}
            <div className="grid grid-cols-7 gap-1 w-full">
                {calendarDays.map((d) => {
                    const isToday = d.isSame(today, "day");
                    const isCurrentMonth = d.isSame(currentMonth, "month");
                    const isSelected = selectedMoment ? d.isSame(selectedMoment, "day") : false;
                    const isDisabled = disabledDate?.(d) ?? false;
                    const content = renderContent?.(d);

                    const colorType = colorDate?.(d) ?? "default";
                    const colorClass = colorType !== "default" && !isSelected ? colorMap[colorType] ?? "" : "";

                    const baseClasses = "h-20 relative rounded text-sm border p-1 transition";

                    const classes = [
                        baseClasses,
                        isCurrentMonth
                            ? "text-gray-800 border-gray-200"
                            : "text-gray-400 border-gray-100",
                        isSelected
                            ? "bg-blue-600 text-white shadow border-blue-600"
                            : (colorType !== "default"
                                ? `${colorClass}`
                                : ""),
                        isToday && !isSelected ? "ring-1 ring-blue-300" : "",
                        isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-blue-500",
                    ]
                        .filter(Boolean)
                        .join(" ");

                    return (
                        <button
                            key={d.toString()}
                            className={classes}
                            onClick={() => handleSelect(d)}
                            aria-pressed={isSelected}
                            disabled={isDisabled}
                            title={d.format("YYYY-MM-DD")}
                        >
                            {content ? (
                                <>
                                    <span
                                        className={`absolute top-1 left-1 w-6 h-6 flex items-center justify-center rounded-full text-xs font-medium ${isSelected
                                            ? "bg-white text-blue-600"
                                            : isToday
                                                ? "bg-blue-500 text-white"
                                                : ""
                                            }`}
                                    >
                                        {d.date()}
                                    </span>
                                    <div className="mt-6 text-xs text-left overflow-hidden">
                                        {content}
                                    </div>
                                </>
                            ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                    <span
                                        className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-medium ${isSelected
                                            ? "bg-white text-blue-600"
                                            : isToday
                                                ? "bg-blue-500 text-white"
                                                : ""
                                            }`}
                                    >
                                        {d.date()}
                                    </span>
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Footer */}
            <div className="mt-4 text-sm text-gray-600 border-t border-gray-200 pt-2">
                {selectedMoment ? (
                    <div>
                        <span className="font-medium">Đã chọn:</span>{" "}
                        {selectedMoment.format(DATE_FORMAT)}
                    </div>
                ) : (
                    <div>Chưa có ngày được chọn.</div>
                )}
            </div>
        </div>
    );
}
